import React from 'react';
import type { DropResult } from '@hello-pangea/dnd';
import { useSelector } from 'store';
import { persistSidebarLayoutToServer } from 'utils/chat-sidebar-server-sync';
import { TEAM_CHAT_SIDEBAR_VIEW_IDS, type TeamChatSidebarViewId } from 'utils/default-chat-sidebar-views';

const STORAGE_KEY = 'funner.chatSidebar.teamViewOrder.v1';

export type ChatSidebarTeamLayoutPreset = 'chronological' | 'operational' | 'custom';

type Persisted = {
  preset: ChatSidebarTeamLayoutPreset;
  order: TeamChatSidebarViewId[];
};

export const TEAM_CHRONOLOGICAL_VIEW_ORDER = [...TEAM_CHAT_SIDEBAR_VIEW_IDS] as TeamChatSidebarViewId[];

export const TEAM_OPERATIONAL_VIEW_ORDER: TeamChatSidebarViewId[] = [
  'team-awaiting-me',
  'team-newly-assigned',
  'team-unassigned-chats',
  'team-waiting-customer',
  'team-my-chats-snoozed',
  'team-chats-completed',
];

function isValidOrder(arr: unknown): arr is TeamChatSidebarViewId[] {
  if (!Array.isArray(arr) || arr.length !== TEAM_CHAT_SIDEBAR_VIEW_IDS.length) return false;
  const set = new Set(arr);
  return TEAM_CHAT_SIDEBAR_VIEW_IDS.every((id) => set.has(id));
}

function loadPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { preset: 'chronological', order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] };
    }
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    const preset =
      parsed.preset === 'operational' || parsed.preset === 'custom' || parsed.preset === 'chronological'
        ? parsed.preset
        : 'chronological';
    if (preset === 'chronological') {
      return { preset, order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] };
    }
    if (preset === 'operational') {
      return { preset, order: [...TEAM_OPERATIONAL_VIEW_ORDER] };
    }
    if (isValidOrder(parsed.order)) {
      return { preset: 'custom', order: parsed.order };
    }
  } catch {
    /* ignore */
  }
  return { preset: 'chronological', order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] };
}

function savePersisted(p: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

/** ולידציה של ערך סדר שהגיע מהשרת (chatSidebarLayout.teamViewOrder) — מבנה זהה ל-Persisted */
function normalizeServerOrder(raw: { preset: string; order: string[] }): Persisted {
  if (raw.preset === 'operational') return { preset: 'operational', order: [...TEAM_OPERATIONAL_VIEW_ORDER] };
  if (raw.preset === 'custom' && isValidOrder(raw.order)) return { preset: 'custom', order: raw.order };
  return { preset: 'chronological', order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] };
}

export type ChatSidebarTeamOrderValue = {
  preset: ChatSidebarTeamLayoutPreset;
  orderedIds: TeamChatSidebarViewId[];
  setChronological: () => void;
  setOperational: () => void;
  onDragEnd: (result: DropResult) => void;
};

const ChatSidebarTeamOrderContext = React.createContext<ChatSidebarTeamOrderValue | null>(null);

function useChatSidebarTeamOrderState(): ChatSidebarTeamOrderValue {
  const [state, setState] = React.useState<Persisted>(() =>
    typeof window === 'undefined'
      ? { preset: 'chronological', order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] }
      : loadPersisted(),
  );

  const stateRef = React.useRef(state);
  stateRef.current = state;

  //* אימוץ חד-פעמי מהשרת: השרת הוא מקור האמת; localStorage נשאר כ-fallback מהיר לעלייה.
  //* שרת בלי ערך + קונפיג שנטען = מיגרציה חד-פעמית של הערך הלוקאלי לשרת.
  const serverOrder = useSelector((s) => s.userConfig?.chatSidebarLayout?.teamViewOrder);
  const configLoaded = useSelector((s) => s.userConfig?.userConfigLoadedFromServer);
  const adoptedRef = React.useRef(false);
  const mutatedRef = React.useRef(false);

  React.useEffect(() => {
    if (adoptedRef.current || !configLoaded || mutatedRef.current) return;
    adoptedRef.current = true;
    if (serverOrder && typeof serverOrder.preset === 'string') {
      const next = normalizeServerOrder(serverOrder);
      setState(next);
      savePersisted(next);
    } else {
      persistSidebarLayoutToServer({ teamViewOrder: stateRef.current });
    }
  }, [configLoaded, serverOrder]);

  const setChronological = React.useCallback(() => {
    const next: Persisted = { preset: 'chronological', order: [...TEAM_CHRONOLOGICAL_VIEW_ORDER] };
    mutatedRef.current = true;
    setState(next);
    savePersisted(next);
    persistSidebarLayoutToServer({ teamViewOrder: next });
  }, []);

  const setOperational = React.useCallback(() => {
    const next: Persisted = { preset: 'operational', order: [...TEAM_OPERATIONAL_VIEW_ORDER] };
    mutatedRef.current = true;
    setState(next);
    savePersisted(next);
    persistSidebarLayoutToServer({ teamViewOrder: next });
  }, []);

  const onDragEnd = React.useCallback((result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;
    setState((prev) => {
      const nextOrder = [...prev.order];
      const [removed] = nextOrder.splice(source.index, 1);
      nextOrder.splice(destination.index, 0, removed);
      const next: Persisted = { preset: 'custom', order: nextOrder };
      mutatedRef.current = true;
      savePersisted(next);
      persistSidebarLayoutToServer({ teamViewOrder: next });
      return next;
    });
  }, []);

  return {
    preset: state.preset,
    orderedIds: state.order,
    setChronological,
    setOperational,
    onDragEnd,
  };
}

export function ChatSidebarTeamOrderProvider({ children }: { children: React.ReactNode }) {
  const value = useChatSidebarTeamOrderState();
  return React.createElement(ChatSidebarTeamOrderContext.Provider, { value }, children);
}

export function useChatSidebarTeamOrder(): ChatSidebarTeamOrderValue {
  const ctx = React.useContext(ChatSidebarTeamOrderContext);
  if (!ctx) {
    throw new Error('useChatSidebarTeamOrder must be used within ChatSidebarTeamOrderProvider');
  }
  return ctx;
}
